import React, { FC, useCallback, useContext, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import { IBasicUserInfo } from '../../resources/models/IUserInfo';
import { UUID } from '../../types/uuid';
import { useCloseDialog } from '../../hooks/useCloseDialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RoleSelectionPartial } from '../InviteUserModal/InviteUserModal';
import Button from '@mui/material/Button';
import { apiOrganization } from '../../resources/api/apiOrganization';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { EOrganizationRole, organizationRoles } from '../../const/roles';
import { CheckboxController } from '../InputControllers/CheckboxController';
import { Control } from 'react-hook-form/dist/types';
import { useResourceChecker } from '../../resources/resource';

interface IProps {
  users: Array<IBasicUserInfo & { owner: boolean; roles: UUID[] }>;
  onRefresh: () => Promise<any>;
}

interface IFormData {
  role_id: string;
  owner: boolean;
}

const { useUpdateMemberRole, useChangeOwner } = apiOrganization;

const isRoleOrganizationAdmin = (roleId: UUID) =>
  roleId === organizationRoles.find((r) => r.role === EOrganizationRole.Admin)?.id;

export const ChangeUserRoleInOrganizationDialog: FC<IProps> = ({ users, onRefresh }) => {
  const { t } = useTranslation();
  const handleApiError = useApiErrorHandler();
  const { organization } = useContext(OrganizationContext);

  const userId = useQueryParam('change-role');
  const closeDialog = useCloseDialog(void 0, ['change-role']);

  const form = useForm<IFormData>();

  const user = users.find(({ id }) => id === userId);

  const { execute } = useUpdateMemberRole(organization?.name, user?.id, user?.roles[0]);
  const {
    execute: changeOwner,
    Guard: GuardChangeOwner,
    resource: resourceChangeOwner,
  } = useChangeOwner(organization?.name, user?.id);
  const resourceChecker = useResourceChecker();

  useEffect(() => {
    if (user) {
      form.reset({
        role_id: user?.roles[0],
        owner: false,
      });
    }
  }, [user]);

  const onSubmit = useCallback(
    async (data: IFormData) => {
      try {
        await execute(data.role_id);
        if (isRoleOrganizationAdmin(data.role_id) && resourceChecker(resourceChangeOwner) && data.owner) {
          await changeOwner();
        }

        await onRefresh();
        closeDialog();
      } catch (e) {
        handleApiError(e);
      }
    },
    [execute, onRefresh, changeOwner, resourceChangeOwner]
  );

  const selectedOrganizationAdmin = isRoleOrganizationAdmin(form.watch('role_id'));

  return (
    <Dialog open={!!user} onClose={closeDialog}>
      <DialogTitle>
        {t('user.changeRoleDialog.title', { name: user ? `${user.first_name} ${user.last_name}` : '' })}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} minWidth={460}>
          <RoleSelectionPartial form={form} />
          <GuardChangeOwner>
            {selectedOrganizationAdmin ? (
              <Grid item xs={12}>
                <CheckboxController
                  control={form.control as unknown as Control}
                  errors={form.formState.errors}
                  label={'Set as organization owner'}
                  name={'owner'}
                />
              </Grid>
            ) : null}
          </GuardChangeOwner>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} variant={'text'}>
          {t('common.dialog.close')}
        </Button>
        <Button onClick={form.handleSubmit(onSubmit)} variant={'contained'}>
          {t('user.changeRoleDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
